import React, { useEffect, useRef, useState } from "react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

// Member sign-in — Healthie ID + email (staff pre-create the account).
export default function MemberLoginModal() {
  const { loginOpen, closeLogin, memberLogin, pendingAction } = useAuth();
  const [healthieId, setHealthieId] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const firstRef = useRef(null);

  useEffect(() => {
    if (!loginOpen) return undefined;
    setError(null);
    const onKey = (e) => e.key === "Escape" && closeLogin();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => firstRef.current?.focus(), 60);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [loginOpen, closeLogin]);

  if (!loginOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await memberLogin(healthieId.trim(), email.trim());
      const action = pendingAction;
      closeLogin();
      setHealthieId("");
      setEmail("");
      if (typeof action === "function") setTimeout(action, 50);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-login-title"
      data-testid="member-login-modal"
    >
      <div
        className="absolute inset-0"
        style={{ background: "rgba(10, 20, 40, 0.62)", backdropFilter: "blur(4px)" }}
        onClick={closeLogin}
        aria-hidden="true"
      />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-white shadow-2xl p-6 sm:p-8"
        style={{ border: "1px solid var(--iw-border)", borderRadius: "3px" }}
      >
        <button
          type="button"
          onClick={closeLogin}
          className="absolute top-4 right-4 inline-flex items-center justify-center w-8 h-8"
          style={{ border: "1px solid var(--iw-border)", borderRadius: "2px", color: "var(--iw-primary)" }}
          aria-label="Close sign-in"
          data-testid="member-login-close-btn"
        >
          <span aria-hidden="true" className="font-mono">✕</span>
        </button>

        <div
          className="iw-mono uppercase mb-1.5"
          style={{ color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }}
        >
          Member Sign-in
        </div>
        <h2 id="member-login-title" className="serif font-bold m-0" style={{ color: "var(--iw-primary)", fontSize: "22px" }}>
          Welcome back
        </h2>
        <p className="text-sm mt-2 mb-5 leading-relaxed" style={{ color: "var(--iw-muted)" }}>
          Sign in with the member ID and email the wellness team set up for you.
        </p>

        <label htmlFor="login-healthie-id" className="iw-mono uppercase block mb-1.5" style={labelStyle}>
          Member ID
        </label>
        <input
          id="login-healthie-id"
          ref={firstRef}
          type="text"
          value={healthieId}
          onChange={(e) => setHealthieId(e.target.value)}
          required
          placeholder="e.g. HLT-1001"
          className="w-full px-4 py-3 mb-4 bg-white text-base outline-none"
          style={inputStyle}
          data-testid="member-login-healthie-input"
        />

        <label htmlFor="login-email" className="iw-mono uppercase block mb-1.5" style={labelStyle}>
          Email
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          className="w-full px-4 py-3 bg-white text-base outline-none"
          style={inputStyle}
          data-testid="member-login-email-input"
        />

        {error && (
          <p className="text-sm mt-4 m-0" style={{ color: "var(--iw-accent-red)" }} data-testid="member-login-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="iw-btn-primary w-full mt-6 justify-center"
          disabled={busy}
          data-testid="member-login-submit-btn"
        >
          {busy ? "Signing in…" : "Sign in"} <span aria-hidden="true">↗</span>
        </button>

        <p className="text-xs mt-5 mb-0 text-center leading-relaxed" style={{ color: "var(--iw-muted)" }}>
          New here? Use <strong>Get Started</strong> to complete your intake — the wellness team
          will email your member ID.
        </p>
      </form>
    </div>
  );
}

const labelStyle = {
  color: "var(--iw-muted)",
  fontSize: "10px",
  letterSpacing: "0.22em",
  fontWeight: 600,
};
const inputStyle = {
  border: "1px solid var(--iw-border)",
  borderRadius: "2px",
  color: "var(--iw-primary)",
  fontFamily: "'IBM Plex Sans', sans-serif",
};
